import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

interface Artist {
  _id: string;
  name: string;
  profileType?: "artist" | "listener";
  description?: string;
  location?: string;
  timezone?: string;
  profileUrl?: string;
  replyToEmail?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ArtistUser {
  _id: string;
  email?: string;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDate(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 py-3 border-b last:border-b-0">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-sm break-all">{value}</div>
    </div>
  );
}

export function ArtistEditOverview({ artistId }: { artistId: string }) {
  const [artist, setArtist] = useState<Artist | null>(null);
  const [users, setUsers] = useState<ArtistUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchArtist() {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/admin/artists/${artistId}`);
        if (response.ok) {
          const data = await response.json();
          setArtist(data.artist);
          setUsers(data.users || []);
        } else if (response.status === 404) {
          setError("Artist not found");
        } else {
          setError("Failed to fetch artist");
        }
      } catch (err) {
        console.error("Failed to fetch artist:", err);
        setError("Failed to fetch artist");
      } finally {
        setIsLoading(false);
      }
    }

    fetchArtist();
  }, [artistId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  if (error || !artist) {
    return (
      <p className="text-muted-foreground">{error || "Artist not found"}</p>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <h2 className="text-sm font-medium mb-2">Artist</h2>
        <div className="border rounded-md px-4">
          <DetailRow label="ID" value={formatValue(artist._id)} />
          <DetailRow label="Name" value={formatValue(artist.name)} />
          <DetailRow label="Profile type" value={formatValue(artist.profileType)} />
          <DetailRow label="Description" value={formatValue(artist.description)} />
          <DetailRow label="Location" value={formatValue(artist.location)} />
          <DetailRow label="Timezone" value={formatValue(artist.timezone)} />
          <DetailRow label="Profile URL" value={formatValue(artist.profileUrl)} />
          <DetailRow label="Reply-to email" value={formatValue(artist.replyToEmail)} />
          <DetailRow label="Created" value={formatDate(artist.createdAt)} />
          <DetailRow label="Updated" value={formatDate(artist.updatedAt)} />
        </div>
      </div>

      <div>
        <h2 className="text-sm font-medium mb-2">Users</h2>
        {users.length > 0 ? (
          <div className="border rounded-md divide-y">
            {users.map((user) => (
              <div key={user._id} className="px-4 py-3">
                <Link
                  to={`/admin/users/${user._id}`}
                  className="text-sm underline underline-offset-4"
                >
                  {user.email || user._id}
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No users have a role for this artist.
          </p>
        )}
      </div>
    </div>
  );
}
